/**
 * NOVA routines server functions: her own routines only, from the signed-in
 * coach. Thin glue over `~/lib/server/nova/routines/engine`.
 *
 * Aliases: nova routines api, pause check-ins, change check-in day, wake routines,
 * routines page, run routine now, remove routine, add routine
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireNovaCoachAuth } from "~/lib/coach-middleware";
import {
  NOVA_ROUTINE_KINDS,
  routineLocalTimeSchema,
  routineWeekdaySchema,
} from "~/lib/shared/nova/routines";

// Type-only re-exports, erased at build, so a component never imports from
// `~/lib/server`.
export type {
  CheckInRunAnswers,
  NovaRoutinesView,
  RoutineListItem,
  RoutineRunRow,
} from "~/lib/server/nova/routines/routines-page";

const routineKindSchema = z.enum(NOVA_ROUTINE_KINDS);

/** Her routines for the Routines page and its tab; `available: false` without a paid plan. */
export const listMyNovaRoutines = createServerFn({ method: "GET" })
  .middleware([requireNovaCoachAuth])
  .handler(async ({ context }) => {
    const { readNovaRoutines } =
      await import("~/lib/server/nova/routines/routines-page");
    return readNovaRoutines(context.coach);
  });

/** The answers of one of her completed check-ins. */
export const getMyNovaRoutineRunAnswers = createServerFn({ method: "GET" })
  .middleware([requireNovaCoachAuth])
  .validator(z.object({ runId: z.string().uuid() }))
  .handler(async ({ data, context }) => {
    const { readCheckInRunAnswers } =
      await import("~/lib/server/nova/routines/routines-page");
    return readCheckInRunAnswers(context.coach, data.runId);
  });

/** Opens this week's run of her routine now; a run of this week is returned as it is. */
export const runMyNovaRoutineNow = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(z.object({ kind: routineKindSchema }))
  .handler(async ({ data, context }): Promise<{ opened: boolean }> => {
    const { openNovaRoutineRunNow } =
      await import("~/lib/server/nova/routines/engine");
    const { opened } = await openNovaRoutineRunNow(context.coach.id, data.kind);
    return { opened };
  });

export const removeMyNovaRoutine = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(z.object({ kind: routineKindSchema }))
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const { removeNovaRoutine } =
      await import("~/lib/server/nova/routines/engine");
    await removeNovaRoutine(context.coach.id, data.kind);
    return { ok: true };
  });

export const addMyNovaRoutine = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(z.object({ kind: routineKindSchema }))
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const { addNovaRoutine } =
      await import("~/lib/server/nova/routines/engine");
    await addNovaRoutine(context.coach.id, data.kind);
    return { ok: true };
  });

/**
 * Called when she opens NOVA: a routine that paused itself after two missed
 * runs turns back on, with a run open at once.
 */
export const wakeMyNovaRoutines = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .handler(async ({ context }): Promise<{ woken: number }> => {
    const { wakeNovaRoutines } =
      await import("~/lib/server/nova/routines/engine");
    return wakeNovaRoutines(context.coach.id);
  });

export const setMyNovaRoutinePaused = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(z.object({ kind: routineKindSchema, paused: z.boolean() }))
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const { setNovaRoutinePaused } =
      await import("~/lib/server/nova/routines/engine");
    await setNovaRoutinePaused(context.coach.id, data.kind, data.paused);
    return { ok: true };
  });

export const changeMyNovaRoutineSchedule = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(
    z.object({
      kind: routineKindSchema,
      weekday: routineWeekdaySchema,
      localTime: routineLocalTimeSchema,
    }),
  )
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const { changeNovaRoutineSchedule } =
      await import("~/lib/server/nova/routines/engine");
    await changeNovaRoutineSchedule(context.coach.id, data.kind, {
      weekday: data.weekday,
      localTime: data.localTime,
    });
    return { ok: true };
  });
